import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Sparkles,
  Heart,
  Trash2,
  CalendarPlus,
  Utensils,
  Wand2,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { createDigitalPrintCheckout, createColoringPageCheckout } from "../lib/api";
import { useAccess } from "../lib/access";
import { useCart } from "../lib/cart";
import PrintOptionsModal from "../components/PrintOptionsModal";
import useSeo from "../lib/useSeo";

const LIBRARY_KEY = "platebuddies_custom_buddies_v1";
const PLANNER_QUEUE_KEY = "platebuddies_planner_queue_v1";

function load() {
  try {
    return JSON.parse(localStorage.getItem(LIBRARY_KEY) || "[]");
  } catch {
    return [];
  }
}

export default function Library() {
  const navigate = useNavigate();
  const [items, setItems] = useState(load);
  const [digitalLoadingId, setDigitalLoadingId] = useState(null);
  const [coloringLoadingId, setColoringLoadingId] = useState(null);
  const [printPickerBuddy, setPrintPickerBuddy] = useState(null);
  const access = useAccess();
  const { addItem } = useCart();

  useSeo({
    title: "My Buddy Library — your saved Plate Buddies",
    description:
      "Every Buddy your family creates lives here. Print them, plan meals with them, or admire them. Personal library only — not indexed publicly.",
    url: "https://platebuddies.com/library",
    noIndex: true,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const refresh = () => setItems(load());
    const onStorage = (e) => {
      if (e.key === LIBRARY_KEY) refresh();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const remove = (id) => {
    const next = items.filter((b) => b.id !== id);
    setItems(next);
    localStorage.setItem(LIBRARY_KEY, JSON.stringify(next));
    toast.success("Removed from your library.");
  };

  const plan = (buddy) => {
    try {
      localStorage.setItem(PLANNER_QUEUE_KEY, JSON.stringify({ buddyId: buddy.id, ts: Date.now() }));
    } catch {
      /* noop */
    }
    navigate("/planner");
  };

  const orderDigitalPrint = async (buddy) => {
    const email = (access?.email || "").trim();
    if (!email) {
      toast.error("Pop your email in on the Create page first — we send the PDF there.");
      navigate("/create");
      return;
    }
    setDigitalLoadingId(buddy.id);
    try {
      const data = await createDigitalPrintCheckout({
        custom_buddy_id: buddy.id,
        email,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't start checkout. Please try again.");
    } finally {
      setDigitalLoadingId(null);
    }
  };

  const orderColoringPage = async (buddy) => {
    const email = (access?.email || "").trim();
    if (!email) {
      toast.error("Pop your email in on the Create page first — we send the PDF there.");
      navigate("/create");
      return;
    }
    setColoringLoadingId(buddy.id);
    try {
      const data = await createColoringPageCheckout({
        custom_buddy_id: buddy.id,
        email,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't start checkout. Please try again.");
    } finally {
      setColoringLoadingId(null);
    }
  };

  const orderPhysicalPrint = (buddy) => {
    addItem({
      product_id: "buddy_print",
      name: `Custom Buddy Print — ${buddy.name}`,
      unit_price: 14.99,
      custom_buddy_id: buddy.id,
      custom_buddy_name: buddy.name,
      custom_buddy_image: buddy.image,
    });
    toast.success(`${buddy.name} print added to cart!`);
    navigate("/cart");
  };

  const addDigitalToCart = (buddy) => {
    addItem({
      product_id: "buddy_print_digital",
      name: `Digital Buddy Print Bundle — ${buddy.name}`,
      unit_price: 5.00,
      custom_buddy_id: buddy.id,
      custom_buddy_name: buddy.name,
      custom_buddy_image: buddy.image,
    });
    toast.success(`${buddy.name}'s digital bundle added to cart!`);
    navigate("/cart");
  };

  const addColoringToCart = (buddy) => {
    addItem({
      product_id: "buddy_coloring_page",
      name: `Coloring Page — ${buddy.name}`,
      unit_price: 3.00,
      custom_buddy_id: buddy.id,
      custom_buddy_name: buddy.name,
      custom_buddy_image: buddy.image,
    });
    toast.success(`${buddy.name}'s coloring page added to cart!`);
    navigate("/cart");
  };

  return (
    <div data-testid="page-library" className="font-body bg-pb-cream min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="blob bg-pb-sun" style={{ width: 420, height: 420, top: -100, left: -120, opacity: 0.4 }} />
        <div className="blob bg-pb-coral" style={{ width: 360, height: 360, top: 60, right: -120, opacity: 0.25 }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">My Buddy Library</p>
              <h1 data-testid="library-heading" className="font-display font-black tracking-tight text-pb-ink mt-3 text-4xl sm:text-5xl md:text-6xl leading-[0.95]">
                {items.length === 0
                  ? "Your library is empty (for now)."
                  : `${items.length} one-of-a-kind Buddy${items.length === 1 ? "" : " buddies"}.`}
              </h1>
              <p className="mt-4 text-pb-soft max-w-xl text-base">
                Every Buddy your family creates lives here forever — drag them into your weekly plan, order them as fridge magnets, or just admire them.
              </p>
            </div>
            <Link
              to="/create"
              data-testid="link-library-create"
              className="inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-6 py-3.5 shadow-[0_12px_28px_rgba(242,106,52,0.35)] hover:scale-105 transition"
            >
              <Wand2 className="h-4 w-4" /> Make a new Buddy
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        {items.length === 0 ? (
          <div
            data-testid="library-empty"
            className="rounded-[2.5rem] border-2 border-dashed border-pb-sky/60 p-12 sm:p-16 text-center bg-white"
          >
            <Sparkles className="h-12 w-12 text-pb-coral mx-auto" />
            <h2 className="font-display font-black text-2xl mt-4">No saved Buddies yet</h2>
            <p className="text-pb-soft mt-2 max-w-md mx-auto">
              Head to the Buddy Generator and tap <strong>Add to my library</strong> after your first creation — it'll show up here.
            </p>
            <Link
              to="/create"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-pb-ink text-white font-extrabold px-6 py-3.5 hover:bg-pb-coral transition"
            >
              <Wand2 className="h-4 w-4" /> Create your first Buddy
            </Link>
          </div>
        ) : (
          <div data-testid="library-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((b) => (
              <article
                key={b.id}
                data-testid={`library-card-${b.id}`}
                className="rounded-[2rem] p-5 border border-white shadow-[0_18px_40px_rgba(28,22,40,0.08)] bg-white relative overflow-hidden hover:-translate-y-0.5 transition"
              >
                <div
                  className="aspect-square rounded-2xl overflow-hidden mb-4 border-4 border-white shadow-md"
                  style={{ background: b.bg || "#FFF6D6" }}
                >
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-black text-xl leading-tight truncate">{b.name}</h3>
                    {b.tagline && (
                      <p className="text-[10px] font-extrabold uppercase tracking-widest mt-0.5 truncate" style={{ color: b.color }}>
                        {b.tagline}
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] font-extrabold uppercase tracking-widest bg-pb-cream text-pb-coral px-2 py-1 rounded-full whitespace-nowrap">
                    Yours
                  </span>
                </div>
                {b.recipe?.title && (
                  <p className="mt-3 text-sm text-pb-ink/70 flex items-center gap-1.5">
                    <Utensils className="h-3.5 w-3.5 text-pb-coral" /> {b.recipe.title}
                  </p>
                )}
                <div className="mt-4 flex gap-2 flex-wrap items-center">
                  <button
                    data-testid={`library-print-${b.id}`}
                    onClick={() => setPrintPickerBuddy(b)}
                    className="text-xs inline-flex items-center gap-1.5 rounded-full bg-pb-coral text-white font-extrabold px-4 py-2.5 hover:scale-105 transition shadow-[0_8px_18px_rgba(242,106,52,0.35)]"
                  >
                    <FileDown className="h-3.5 w-3.5" /> Print
                  </button>
                  <button
                    data-testid={`library-plan-${b.id}`}
                    onClick={() => plan(b)}
                    className="text-xs inline-flex items-center gap-1.5 rounded-full bg-white text-pb-ink font-extrabold px-3 py-2 hover:text-pb-coral border-2 border-pb-sky/40 hover:border-pb-coral transition"
                  >
                    <CalendarPlus className="h-3.5 w-3.5" /> Plan a meal
                  </button>
                  <button
                    data-testid={`library-delete-${b.id}`}
                    onClick={() => remove(b.id)}
                    className="text-xs inline-flex items-center justify-center rounded-full bg-white text-pb-soft font-bold w-9 h-9 border border-pb-sky/30 hover:text-pb-coral hover:border-pb-coral transition ml-auto"
                    aria-label={`Remove ${b.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-12 rounded-[2rem] bg-white p-6 sm:p-8 border border-pb-sky/30 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div className="flex items-center gap-3">
              <Heart className="h-6 w-6 text-pb-coral fill-pb-coral" />
              <div>
                <p className="font-display font-black text-lg">Got more ideas?</p>
                <p className="text-sm text-pb-soft">Each Buddy comes with its own coordinated recipe.</p>
              </div>
            </div>
            <Link
              to="/create"
              className="inline-flex items-center gap-2 rounded-full bg-pb-ink text-white font-extrabold px-6 py-3 hover:bg-pb-coral transition"
            >
              <Wand2 className="h-4 w-4" /> Generate another
            </Link>
          </div>
        )}
      </section>

      <PrintOptionsModal
        open={!!printPickerBuddy}
        buddy={printPickerBuddy}
        loadingDigital={!!digitalLoadingId && digitalLoadingId === printPickerBuddy?.id}
        loadingColoring={!!coloringLoadingId && coloringLoadingId === printPickerBuddy?.id}
        onClose={() => setPrintPickerBuddy(null)}
        onPickDigital={(b) => {
          setPrintPickerBuddy(null);
          orderDigitalPrint(b);
        }}
        onPickColoring={(b) => {
          setPrintPickerBuddy(null);
          orderColoringPage(b);
        }}
        onPickPhysical={(b) => {
          setPrintPickerBuddy(null);
          orderPhysicalPrint(b);
        }}
        onAddDigital={(b) => {
          setPrintPickerBuddy(null);
          addDigitalToCart(b);
        }}
        onAddColoring={(b) => {
          setPrintPickerBuddy(null);
          addColoringToCart(b);
        }}
      />

      <Footer />
    </div>
  );
}
